import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { jwtDecode } from 'jwt-decode';
import { useTable, useSortBy, useExpanded } from 'react-table';
import 'bootstrap/dist/css/bootstrap.min.css';

const ENDPOINT_MAP = {
  aprobados: '/turnos/aprobados-historico',
  rechazados: '/turnos/rechazados-historico',
};

const AdminTurnosAprobados = () => {
  const navigate = useNavigate();
  const [turnos, setTurnos] = useState([]);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedRows, setExpandedRows] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [vista, setVista] = useState('aprobados');

  const verificarRol = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      const decoded = jwtDecode(token);
      if (decoded.rol !== 'admin') navigate('/');
    } catch (error) {
      console.error('❌ Error al decodificar el token:', error);
      navigate('/login');
    }
  }, [navigate]);

  const parseTurnos = (lista) =>
    (lista || []).map((turno) => {
      const rawDecisionTs =
        turno.decision_at ||
        turno.aprobado_en ||
        turno.rechazado_en ||
        turno.estado_cambiado_en ||
        turno.updatedAt ||
        turno.updated_at ||
        null;

      return {
        ...turno,
        fecha_turno: turno.fecha_turno ? new Date(`${turno.fecha_turno}T00:00:00`) : null,
        proveedor_bultos: Array.isArray(turno.proveedor_bultos)
          ? turno.proveedor_bultos
          : typeof turno.proveedor_bultos === 'string'
            ? (() => { try { return JSON.parse(turno.proveedor_bultos); } catch { return []; } })()
            : [],
        documentos: Array.isArray(turno.documentos) ? turno.documentos : [],
        createdAt: turno.createdAt ? new Date(turno.createdAt) : null,
        decision_at: rawDecisionTs ? new Date(rawDecisionTs) : null,
      };
    });

  const buildUrl = (p, v) => `${ENDPOINT_MAP[v]}?page=${p}&limit=10`;

  useEffect(() => {
    verificarRol();
  }, [verificarRol]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setError('');
        const token = localStorage.getItem('token');
        const url = buildUrl(page, vista);
        const { data } = await api.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!isMounted) return;
        setTurnos(parseTurnos(data.turnos));
        setTotalPages(data.totalPages || 1);
      } catch (e) {
        console.error('❌ Error al obtener turnos:', e);
        if (isMounted) setError(`Error al obtener turnos ${vista}.`);
      }
    })();
    return () => { isMounted = false; };
  }, [page, vista]);

  const toggleExpand = (id) => {
    setExpandedRows((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]));
  };

  const handleBorrarTurno = useCallback(
    async (turnoId) => {
      const textoConfirm =
        vista === 'aprobados'
          ? '¿Seguro que querés borrar este turno aprobado? Liberará la fecha y hora.'
          : '¿Seguro que querés borrar este turno rechazado? (No libera horarios, solo oculta del histórico).';
      if (!window.confirm(textoConfirm)) return;

      try {
        setDeletingId(turnoId);
        const token = localStorage.getItem('token');
        await api.put(
          `/turnos/${turnoId}/eliminar`,
          { eliminado: 1 },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const txtOk =
          vista === 'aprobados'
            ? 'Turno borrado correctamente. El horario quedó disponible.'
            : 'Turno rechazado borrado del histórico correctamente.';
        setMensaje(txtOk);
        setTimeout(() => setMensaje(''), 3000);

        const url = buildUrl(page, vista);
        const { data } = await api.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTurnos(parseTurnos(data.turnos));
        setTotalPages(data.totalPages || 1);
      } catch (err) {
        console.error('❌ Error al borrar turno:', err);
        setError('Error al borrar el turno.');
      } finally {
        setDeletingId(null);
      }
    },
    [page, vista]
  );

  const data = React.useMemo(() => turnos, [turnos]);

  const columns = React.useMemo(() => {
    const base = [
      { Header: 'ID', accessor: 'id' },
      {
        Header: 'Fecha',
        accessor: (row) => row.fecha_turno?.toLocaleDateString() || 'Fecha no disponible',
        id: 'fecha_turno',
      },
      { Header: 'Hora Desde', accessor: 'hora_desde' },
      { Header: 'Hora Hasta', accessor: 'hora_hasta' },

      {
        Header: 'Decisión (fecha y hora)',
        id: 'decision_at',
        accessor: (row) => (row.decision_at ? row.decision_at.getTime() : 0),
        Cell: ({ row }) => {
          const dt = row.original.decision_at;
          if (!dt) return '—';
          return dt.toLocaleString();
        },
      },
    ];

    if (vista === 'rechazados') {
      base.push({
        Header: 'Motivo de rechazo',
        id: 'motivo_rechazo',
        accessor: (row) =>
          row.devolucion ||
          row.motivo ||
          row.motivo_rechazo ||
          row.rechazo_motivo ||
          '-',
        Cell: ({ value }) => value || '-',
      });
    }

    base.push(
      {
        Header: 'Transportista',
        accessor: (row) => row.transportista?.nombre || 'Sin nombre',
        id: 'transportista_nombre',
      },
      { Header: 'Observaciones', accessor: 'observaciones' },
      {
        Header: 'Archivos',
        accessor: 'documentos',
        Cell: ({ row }) => {
          const id = row.original.id + '-docs';
          const isExpanded = expandedRows.includes(id);
          return (
            <div>
              <button
                className="btn btn-sm btn-outline-secondary mb-2"
                onClick={() => toggleExpand(id)}
              >
                {isExpanded ? '🔽 Ver menos' : '▶️ Ver archivos'}
              </button>
              {isExpanded && (
                <ul className="list-unstyled">
                  {row.original.documentos?.length > 0 ? (
                    row.original.documentos.map((doc, i) => (
                      <li key={i}>
                        <a href={doc.ruta} target="_blank" rel="noopener noreferrer" download>
                          📄 {doc.nombre_original}
                        </a>
                      </li>
                    ))
                  ) : (
                    <li className="text-muted">Sin documentos</li>
                  )}
                </ul>
              )}
            </div>
          );
        },
      },
      {
        Header: 'Detalle',
        accessor: 'proveedor_bultos',
        Cell: ({ row }) => {
          const id = row.original.id;
          const isExpanded = expandedRows.includes(id);
          return (
            <div>
              <button
                className="btn btn-sm btn-outline-secondary mb-2"
                onClick={() => toggleExpand(id)}
              >
                {isExpanded ? '🔽 Ocultar Detalle' : '▶️ Ver Detalle'}
              </button>
              {isExpanded && (
                <div>
                  {row.original.proveedor_bultos?.length > 0 ? (
                    row.original.proveedor_bultos.map((prov, i) => (
                      <div key={i} className="mb-2 text-start">
                        <strong>{prov.nombre}</strong>
                        <ul>
                          <li><strong>Facturas:</strong> {prov.facturas?.join(', ') || '-'}</li>
                          <li><strong>Orden Compra:</strong> {prov.orden_compra || '-'}</li>
                          <li><strong>Remito:</strong> {prov.remito || '-'}</li>
                          <li>
                            <strong>Bultos:</strong>
                            <ul className="mb-0">
                              {Object.entries(prov.bultos || {}).map(([tipo, cantidad]) =>
                                Number(cantidad) > 0 ? (
                                  <li key={tipo}><strong>{tipo}:</strong> {cantidad}</li>
                                ) : null
                              )}
                            </ul>
                          </li>
                        </ul>
                      </div>
                    ))
                  ) : (
                    <span className="text-muted">Sin datos</span>
                  )}
                </div>
              )}
            </div>
          );
        },
      },
      {
        Header: 'Acciones',
        id: 'acciones',
        Cell: ({ row }) => {
          const id = row.original.id;
          const loading = deletingId === id;
          return (
            <button
              className="btn btn-danger btn-sm"
              onClick={() => handleBorrarTurno(id)}
              disabled={loading}
              title={
                vista === 'aprobados'
                  ? 'Borrar turno aprobado (libera fecha y hora)'
                  : 'Borrar turno rechazado del histórico'
              }
            >
              {loading ? 'Borrando...' : '🗑️ Borrar'}
            </button>
          );
        },
      }
    );

    return base;
  }, [expandedRows, deletingId, handleBorrarTurno, vista]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data }, useSortBy, useExpanded);

  const titulo = vista === 'aprobados' ? 'Turnos Aprobados' : 'Turnos Rechazados';
  const textoVacio =
    vista === 'aprobados' ? 'No hay turnos aprobados.' : 'No hay turnos rechazados.';

  const handleCambioVista = (nueva) => {
    if (nueva === vista) return;
    setExpandedRows([]);
    setPage(1);
    setVista(nueva);
  };

  return (
    <div className="container mt-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">{titulo}</h2>

        <div className="d-flex gap-2">
          <div className="btn-group me-2" role="group" aria-label="Selector de vista">
            <button
              type="button"
              className={`btn btn-sm ${vista === 'aprobados' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => handleCambioVista('aprobados')}
            >
              ✅ Aprobados
            </button>
            <button
              type="button"
              className={`btn btn-sm ${vista === 'rechazados' ? 'btn-danger' : 'btn-outline-danger'}`}
              onClick={() => handleCambioVista('rechazados')}
            >
              ❌ Rechazados
            </button>
          </div>

          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin-turnos')}>
            ⬅️ Volver a Administración
          </button>
        </div>
      </div>

      {mensaje && <div className="alert alert-success">{mensaje}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      {turnos.length === 0 ? (
        <p className="text-center">{textoVacio}</p>
      ) : (
        <>
          <table className="table table-bordered table-striped text-center" {...getTableProps()}>
            <thead className="table-dark">
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                      {column.render('Header')}
                      <span>
                        {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="d-flex justify-content-center mt-3">
            <button
              className="btn btn-outline-secondary me-2"
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1}
            >
              ⬅️ Anterior
            </button>
            <span className="align-self-center">
              Página {page} de {totalPages}
            </span>
            <button
              className="btn btn-outline-secondary ms-2"
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={page === totalPages}
            >
              Siguiente ➡️
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminTurnosAprobados;

